import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useRegisterSteps } from "@/api/mutations/register";
import { useUser } from "@/api/queries/user";
import { GenderSelector, IntoSelector, PlacesAutocomplete } from "@/components/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useAuthStore } from "@/stores/auth-store";
import { useToastStore } from "@/stores/toast-store";
import type { RegisterSteps } from "@/types/user";

export const Route = createFileRoute("/_setup/register-steps")({
	component: RegisterStepsPage,
});

function hasActiveSteps(steps: RegisterSteps | null | undefined): boolean {
	if (!steps) return false;
	return steps.dob || steps.place || steps.searchFor || steps.gender;
}

function RegisterStepsPage() {
	const navigate = useNavigate();
	const { data: user, isLoading: userLoading } = useUser();
	const { pendingSteps, setPendingSteps } = useAuthStore();

	usePageMeta({ noIndex: true });
	const { addToast } = useToastStore();
	const registerStepsMutation = useRegisterSteps();

	// Steps aus User-Daten oder Fallback aus Store
	const steps = user?.registerSteps ?? pendingSteps;

	// Form-State
	const [dob, setDob] = useState("");
	const [place, setPlace] = useState<{ id: number; name: string } | null>(null);
	const [into, setInto] = useState<"m" | "f" | "m+f" | null>(null);
	const [gender, setGender] = useState<"m" | "f" | null>(null);
	const [formError, setFormError] = useState<string | null>(null);

	// Redirect wenn keine Steps nötig
	if (!userLoading && !hasActiveSteps(steps)) {
		throw redirect({ to: "/search" });
	}

	// Während User geladen wird
	if (userLoading) {
		return (
			<div className="space-y-6">
				<div className="text-center">
					<h1 className="text-2xl font-bold text-text">Profil vervollständigen</h1>
					<p className="mt-2 text-base text-text-secondary">Einen Moment...</p>
				</div>
			</div>
		);
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setFormError(null);

		// Validierung
		if (steps?.dob && !dob) {
			setFormError("Bitte gib dein Geburtsdatum an.");
			return;
		}
		if (steps?.place && !place) {
			setFormError("Bitte wähle deinen Ort.");
			return;
		}
		if (steps?.searchFor && !into) {
			setFormError("Bitte wähle wen du suchst.");
			return;
		}
		if (steps?.gender && !gender) {
			setFormError("Bitte wähle dein Geschlecht.");
			return;
		}

		// Body zusammenbauen (nur gesetzte Felder)
		const body: Record<string, string | number> = {};
		if (steps?.dob && dob) {
			body.dob = dob;
		}
		if (steps?.place && place) {
			body.place = place.id;
		}
		if (steps?.searchFor && into) {
			body.into = into;
		}
		if (steps?.gender && gender) {
			body.gender = gender;
		}

		try {
			const result = await registerStepsMutation.mutateAsync(body);

			if (result.state === "error") {
				setFormError(result.message ?? "Ein Fehler ist aufgetreten.");
				return;
			}

			addToast({
				type: "success",
				message: "Profil vervollständigt",
			});
			setPendingSteps(null);
			navigate({ to: "/search" });
		} catch {
			setFormError("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
		}
	}

	return (
		<div className="space-y-6">
			<div className="text-center">
				<h1 className="text-2xl font-bold text-text">Profil vervollständigen</h1>
				<p className="mt-2 text-base text-text-secondary">Noch ein paar Angaben und es geht los.</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-4" noValidate>
				{steps?.gender && <GenderSelector value={gender} onChange={setGender} />}

				{steps?.searchFor && <IntoSelector value={into} onChange={setInto} />}

				{steps?.dob && (
					<Input
						type="date"
						label="Geburtsdatum"
						value={dob}
						onChange={(e) => setDob(e.target.value)}
						fullWidth
					/>
				)}

				{steps?.place && <PlacesAutocomplete value={place} onChange={setPlace} />}

				<Button type="submit" variant="primary" fullWidth loading={registerStepsMutation.isPending}>
					Speichern
				</Button>

				{formError && (
					<p className="text-error text-base text-center" role="alert">
						{formError}
					</p>
				)}
			</form>
		</div>
	);
}
